import type { RefObject } from "react";
import { useState } from "react";
import type { BuildAndBuyDispatchType } from "../../../redux/store";
import { useBnbSelector } from "../../../redux/store";
import CarDataConsolidator from "../../../../../shared-logic/utils/classes/CarDataConsolidator";
import type { PriceType, WinterWheelType } from "../../../../../../common-deprecated/types/CommonTypes";
import { removeWinterWheel, setWinterWheel } from "../../../redux/actions/BasketActions";
import type { WinterWheelCategoryType } from "../../../../../../common-deprecated/constants";
import { STUDDED } from "../../../../../../common-deprecated/constants";
import { getCarDescription } from "../../../utils/typedUtils";
import { showWinterWheel } from "../../../../../../common-deprecated/redux/actions/UtilActions";
import { trackCustomizeWinterWheel, trackWinterWheelsCancel } from "../../../utils/tracking";
import type { ImageOptionsType } from "../../../../../../common-deprecated/settings/utils/ccisSettingUtils";
import { addPrices } from "../../../../../shared-logic/utils/priceUtils";
import { useDispatch } from "../../../../../../shared-logic/redux/store";
import { getCarImageFromConfig } from "../../../../../shared-logic/utils/typedUtils";

type DataType = {
    // header
    previewImage: {
        alt: string;
        src: string; // returns a car preview image with the current selected winterwheel
    };
    cheapestWinterWheel: WinterWheelType;

    // wheel type / category tabs
    hasMultipleWheelTypes: boolean;
    wheelType: WinterWheelCategoryType;
    selectWheelType: (type: WinterWheelCategoryType) => void;
    wheelCategories: WinterWheelCategoryType[];

    // wheel selection
    winterWheels: WinterWheelType[]; // sorted by price
    selectedWinterWheel: WinterWheelType;
    selectWinterWheel: (winterWheel: WinterWheelType) => void;

    // installation
    includeInstallation: boolean;
    updateInstallationState: (install: boolean) => void;
    totalPrice: PriceType;

    // modal actions
    confirm: () => void;
    cancel: () => void;
};

/**
 * Contains all logic used in the WinterWheelsModals.
 * One major thing to keep in mind here: selecting a winter wheel (even before clicking confirm) immediately adds it to the basket.
 * This means "confirm" is in most cases a basic closing of the modal, while cancel will attempt to reset the previously selected wheel.
 */
const useWinterWheelsModal = (
    closeModal: () => void,
    footerRef: RefObject<HTMLElement>,
    imageFrame: number,
    imageOptions?: ImageOptionsType,
): DataType => {
    const dispatch = useDispatch<BuildAndBuyDispatchType>();

    const wheelInBasket = useBnbSelector((state) => state.basket.winterWheel);
    const winterWheels = useBnbSelector((state) =>
        CarDataConsolidator.getWinterWheels(state.items.winterWheels, state.basket.colour, state.basket.upholstery),
    );
    const previewImageAlt = useBnbSelector((state) => getCarDescription(state.car));
    const previewImageSrc = useBnbSelector((state) =>
        getCarImageFromConfig(
            state.commonSettings,
            "day-exterior",
            imageFrame,
            state.car.model.id,
            state.car.id,
            imageOptions,
            state.basket.colour,
            state.basket.upholstery,
            state.basket.wheel,
            state.basket.packs,
            CarDataConsolidator.getAllRelevantBasketExtras(state.basket, state.items),
            false,
            state.basket.winterWheel,
        ),
    );

    const selectedWinterWheel = wheelInBasket || winterWheels[0]; // always display a winterwheel as selected
    const hasMultipleWheelTypes =
        winterWheels.map((wheel) => wheel.wheelCategory).filter((wheel, index, list) => list.indexOf(wheel) === index)
            .length > 1;

    const [wheelSelectedWhenOpened] = useState<WinterWheelType | null>(wheelInBasket); // this state will keep the initial value
    const [wheelType, setWheelType] = useState<WinterWheelCategoryType>(selectedWinterWheel.wheelCategory ?? "STUDDED");
    const [includeInstallation, setIncludeInstallation] = useState(selectedWinterWheel.installTheWheels);

    const monthly: number = useBnbSelector((state) => {
        return typeof state.finance.itemRates[selectedWinterWheel.id] !== "undefined"
            ? Number(state.finance.itemRates[selectedWinterWheel.id])
            : 0;
    });
    const totalPrice =
        includeInstallation && selectedWinterWheel.installationCost
            ? addPrices([{ ...selectedWinterWheel.price, monthly }, selectedWinterWheel.installationCost])
            : { ...selectedWinterWheel.price, monthly };

    const addWinterWheelToBasket = (winterWheel: WinterWheelType): void => {
        dispatch(setWinterWheel(winterWheel, true));
    };

    // ----------------------------------------------------------------------
    // Actions
    // ----------------------------------------------------------------------

    const selectWheelType = (type: WinterWheelCategoryType): void => {
        setWheelType(type);
    };

    const wheelCategories = new Set<WinterWheelCategoryType>(
        winterWheels.map((winterWheel) => {
            return winterWheel.wheelCategory ?? STUDDED;
        }),
    );

    const selectWinterWheel = (winterWheel: WinterWheelType): void => {
        // only add it when there isn't one set, and if there is one set check if the one we are trying to add is not the same one.
        if (!wheelInBasket || wheelInBasket.id !== winterWheel.id) {
            if (footerRef.current) {
                footerRef.current.scrollIntoView({
                    behavior: "smooth",
                });
            }
            addWinterWheelToBasket(winterWheel);
        }
    };

    const updateInstallationState = (included: boolean): void => {
        setIncludeInstallation(included);
    };

    const confirm = (): void => {
        const wheel: WinterWheelType = { ...selectedWinterWheel, installTheWheels: includeInstallation };
        trackCustomizeWinterWheel(includeInstallation);
        addWinterWheelToBasket(wheel);
        closeModal();
    };

    const cancel = (): void => {
        trackWinterWheelsCancel();
        if (
            wheelInBasket &&
            wheelSelectedWhenOpened &&
            (wheelInBasket.id !== wheelSelectedWhenOpened.id || wheelInBasket.installTheWheels !== includeInstallation)
        ) {
            // user has changed their previous selection but cancels new selection
            // so we set the winterwheel that was set when we opened the modal
            addWinterWheelToBasket(wheelSelectedWhenOpened);
        } else if (!wheelSelectedWhenOpened) {
            // if there was no winterwheel selected before we clear the winterwheel completely and disable the preview/snow animation
            dispatch(removeWinterWheel());
            dispatch(showWinterWheel(false));
        }
        closeModal();
    };

    return {
        previewImage: {
            alt: previewImageAlt,
            src: previewImageSrc,
        },
        hasMultipleWheelTypes,
        cheapestWinterWheel: winterWheels[0],
        wheelType,
        selectWheelType,
        wheelCategories: Array.from(wheelCategories).sort((a, b) => a.localeCompare(b)),
        winterWheels,
        selectedWinterWheel,
        selectWinterWheel,
        includeInstallation,
        updateInstallationState,
        totalPrice,
        confirm,
        cancel,
    };
};

export default useWinterWheelsModal;
